<template>
  <div class="ai-model-fields">
    <div class="generation-cloud-title generation-cloud-title--left generation-cloud-title--left-sub">
      AI и модель
    </div>

    <div class="mb-2">
      <label for="aiProvider" class="left-headers form-label mb-0">
        Нейросеть{{ required ? '*' : '' }}
      </label>
      <select id="aiProvider" class="form-select form-select-sm" :required="required"
        :value="aiProvider" @change="emit('update:aiProvider', $event.target.value)">
        <option disabled value="">Выберите провайдера</option>
        <option v-for="prov in aiProviders" :key="prov" :value="prov">{{ prov }}</option>
      </select>
    </div>

    <div class="mb-2">
      <label for="aiModel" class="left-headers form-label mb-0">
        Модель нейросети{{ required ? '*' : '' }}
      </label>
      <select id="aiModel" class="form-select form-select-sm"
        :disabled="aiModels.length === 0" :required="required"
        :value="aiModel" @change="emit('update:aiModel', $event.target.value)">
        <option disabled value="">Выберите модель</option>
        <option v-for="m in aiModels" :key="m" :value="m">{{ m }}</option>
      </select>
    </div>

    <div class="mb-2" v-if="reasoningSetting">
      <label class="left-headers form-label mb-0">
        {{ reasoningSetting.description || 'Reasoning' }}
      </label>
      <template v-if="reasoningSetting.settingType === 'DROP_DOWN'">
        <select class="form-select form-select-sm"
          :value="settings[reasoningSetting.settingKey]"
          @change="setSetting($event.target.value)">
          <option v-for="opt in (reasoningSetting.metadata?.options || [])" :key="opt" :value="opt">{{ opt }}</option>
        </select>
      </template>
      <template v-else-if="reasoningSetting.settingType === 'MULTI_SELECT'">
        <select class="form-select form-select-sm" multiple
          :size="Math.min(8, Math.max(5, reasoningSetting.metadata?.options?.length || 5))"
          :value="toArray(settings[reasoningSetting.settingKey])"
          @change="setSetting(Array.from($event.target.selectedOptions || []).map(o => o.value))">
          <option v-for="opt in (reasoningSetting.metadata?.options || [])" :key="opt" :value="opt">{{ opt }}</option>
        </select>
      </template>
      <template v-else-if="reasoningSetting.settingType === 'SLIDER'">
        <div class="slider-wrapper">
          <input type="range" class="form-range"
            :min="reasoningSetting.metadata?.min" :max="reasoningSetting.metadata?.max"
            :step="reasoningSetting.metadata?.step"
            :value="settings[reasoningSetting.settingKey]"
            @input="setSetting(Number($event.target.value))" />
          <span class="slider-value">{{ settings[reasoningSetting.settingKey] }}</span>
        </div>
      </template>
      <template v-else-if="reasoningSetting.settingType === 'CHECKBOX'">
        <div class="form-check generation-checkbox mt-1">
          <input id="gen-reasoning-field" class="form-check-input" type="checkbox"
            :checked="toBool(settings[reasoningSetting.settingKey])"
            @change="setSetting($event.target.checked)" />
          <label class="form-check-label" for="gen-reasoning-field">Включено</label>
        </div>
      </template>
      <template v-else-if="reasoningSetting.settingType === 'TEXT_AREA'">
        <textarea class="form-control form-control-sm" rows="3"
          :value="settings[reasoningSetting.settingKey]"
          @input="setSetting($event.target.value)"></textarea>
      </template>
      <template v-else>
        <input type="text" class="form-control form-control-sm"
          :value="settings[reasoningSetting.settingKey]"
          @input="setSetting($event.target.value)" />
      </template>
    </div>
  </div>
</template>

<script setup>
// Presentational block for AI provider / model / reasoning. Uses v-model-style
// props/emits (aiProvider, aiModel, settings) so the parent owns the state and
// this component never mutates props directly.
const props = defineProps({
  aiProvider: { type: String, default: '' },
  aiModel: { type: String, default: '' },
  settings: { type: Object, default: () => ({}) },
  required: { type: Boolean, default: false },
  aiProviders: { type: Array, default: () => [] },
  aiModels: { type: Array, default: () => [] },
  reasoningSetting: { type: Object, default: null },
})
const emit = defineEmits(['update:aiProvider', 'update:aiModel', 'update:settings'])

function setSetting(value) {
  if (!props.reasoningSetting) return
  emit('update:settings', { ...props.settings, [props.reasoningSetting.settingKey]: value })
}

function toArray(v) {
  if (Array.isArray(v)) return v
  if (v == null || v === '') return []
  return [v]
}

function toBool(v) {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v === 1
  if (typeof v === 'string') return ['true', '1', 'yes', 'on'].includes(v.trim().toLowerCase())
  return Boolean(v)
}
</script>

<style scoped>
.generation-cloud-title {
  display: inline-flex;
  align-items: center;
  margin: 0 0 10px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: linear-gradient(180deg, #f2f7ff 0%, #eaf2ff 100%);
  color: #2f4f88;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: 0 4px 10px rgba(49, 94, 227, 0.08);
}

.generation-cloud-title--left { margin-bottom: 10px; }
.generation-cloud-title--left-sub { margin: 8px 0 10px; }

.left-headers {
  padding-left: 1px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #4d5f82;
  text-transform: uppercase;
}

.ai-model-fields .form-control,
.ai-model-fields .form-select { border-radius: 10px; }

.ai-model-fields .form-control:focus,
.ai-model-fields .form-select:focus {
  border-color: #95b8ff;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.14);
}

.slider-wrapper { display: flex; gap: 10px; align-items: center; }
.slider-wrapper .form-range { flex: 1; }
.slider-wrapper .slider-value { min-width: 28px; white-space: nowrap; font-weight: 600; color: #354b74; }

.generation-checkbox .form-check-input { border-radius: 6px; width: 1.1rem; height: 1.1rem; }
.generation-checkbox .form-check-input:focus { box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.14); }
.generation-checkbox .form-check-label { margin-left: 4px; font-size: 13px; color: #43567a; }
</style>
